import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { STAR_OPTIONS } from 'utils/searchFieldLook';
import FieldTrigger, {
  FieldVariant,
  fieldWrapperClassName,
  panelPositionClassName,
} from './FieldTrigger';

interface IStarsDropdownProps {
  // The least stars a hotel needs; 0 is any.
  value: number;
  onChange: (v: number) => void;
  variant?: FieldVariant;
}

// WHIT-497: the stars as a setting in the row above the fields, as the flight
// tab's trip type is, instead of five buttons in the advanced rows.
const StarsDropdown: React.FC<IStarsDropdownProps> = ({
  value,
  onChange,
  variant = 'field',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = (stars: number) =>
    stars === 0 ? t('Any') : t('{{count}}+ stars', { count: stars });
  // In the settings row "Any" alone would not say what it is about.
  const label =
    value === 0 && variant === 'setting' ? t('Star rating') : name(value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={fieldWrapperClassName(variant)}>
      <FieldTrigger
        label={t('Star rating')}
        showLabel
        variant={variant}
        open={open}
        filled={value > 0}
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div
          className={`absolute z-50 top-full mt-1 ${panelPositionClassName(
            variant,
          )} min-w-[200px] bg-white rounded-xl shadow-lg border border-tt-gray-purple`}>
          <div className="py-1">
            {STAR_OPTIONS.map(stars => (
              <button
                key={stars}
                type="button"
                onClick={() => {
                  onChange(stars);
                  setOpen(false);
                }}
                className={`w-full min-h-11 text-left px-4 py-2.5 text-sm hover:bg-tt-verylight-purple transition-colors flex items-center gap-2 ${
                  value === stars ? 'font-semibold text-tt-dark-purple' : ''
                }`}>
                {value === stars && <span className="text-tt-yellow">✓</span>}
                {stars > 0 && (
                  <span aria-hidden className="text-tt-yellow">
                    {'★'.repeat(stars)}
                  </span>
                )}
                {name(stars)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StarsDropdown;
